import type { CityConfig } from "@/pages/city-template/types";

export const westLinnConfig: CityConfig = {
  city: "West Linn",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "west-linn",
  route: "/west-linn",
  title: "Emergency Passport West Linn, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in West Linn, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for West Linn and Clackamas County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in West Linn, Oregon",
  heroSubtitle: "Passport expired before your Europe trip? Flying out of PDX in days? Fast Passport Center helps West Linn travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=West%20Linn%20Oregon%20scenic%20view%20overlooking%20Willamette%20River%20with%20lush%20green%20hills%2C%20upscale%20suburban%20neighborhood%20with%20tree-lined%20streets%2C%20Pacific%20Northwest%20greenery%2C%20soft%20golden%20hour%20lighting%2C%20warm%20amber%20and%20green%20tones%2C%20Willamette%20Falls%20visible%20in%20distance%2C%20photorealistic%20landscape%20photography%2C%20serene%20affluent%20suburban%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=west-linn-hero-01&orientation=landscape",
  heroImageAlt: "West Linn Oregon scenic view overlooking Willamette River with lush green hills",
  geoPosition: "45.3654;-122.6126",
  geoPlacename: "West Linn, Oregon",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "West Linn", zips: "97068" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Oregon City", zips: "97045" },
    { name: "Milwaukie", zips: "97222, 97267" },
    { name: "Tualatin", zips: "97062" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Gladstone", zips: "97027" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Clackamas", zips: "97015" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Beaverton", zips: "97002–97078" },
    { name: "Hillsboro", zips: "97122–97124" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Sherwood", zips: "97140" },
    { name: "Newberg", zips: "97132" },
    { name: "Wilsonville", zips: "97070" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in West Linn in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from West Linn", desc: "FedEx and UPS locations throughout West Linn and the Portland metro offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Jennifer L.",
      location: "West Linn, OR",
      rating: 5,
      text: "Our family planned a spring break trip to Italy and my passport was expired. Fast Passport Center handled the renewal from West Linn in 6 days. I shipped from the FedEx on Willamette Falls Drive and had it back before our flight.",
      service: "Passport Renewal",
    },
    {
      name: "Robert H.",
      location: "Lake Oswego, OR",
      rating: 5,
      text: "My son needed his first passport for a study abroad program in Spain. Fast Passport Center made the child passport process so straightforward. We visited the acceptance facility in Oregon City and they handled the rest. Passport arrived in 8 days.",
      service: "Child Passport",
    },
    {
      name: "Michelle C.",
      location: "West Linn, OR",
      rating: 5,
      text: "Business conference in London came up with two weeks notice. My passport had 4 months left and the airline required 6 months validity. Fast Passport Center got me a renewed passport in 5 days. Saved my career trip.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "west-linn-post",
      name: "West Linn Post Office",
      type: "USPS Passport Acceptance",
      address: "4380 Willamette Falls Dr",
      city: "West Linn",
      state: "OR",
      zip: "97068",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Willamette Falls area, central West Linn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in West Linn. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "610 7th St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Oregon City — about 3 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Oregon City option for southern West Linn and historic downtown area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county",
      name: "Clackamas County Clerk",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8:30am–4:30pm (by appointment)",
      distance: "Oregon City — County Government Center",
      website: "https://www.clackamas.us/recording",
      websiteLabel: "Clackamas County Clerk",
      notes: "County clerk passport acceptance. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "lake-oswego-post",
      name: "Lake Oswego Post Office",
      type: "USPS Passport Acceptance",
      address: "4000 Kruse Way Pl",
      city: "Lake Oswego",
      state: "OR",
      zip: "97035",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Lake Oswego — about 5 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Lake Oswego option for western West Linn and affluent Portland suburb residents.",
      icon: "ri-mail-line",
    },
    {
      id: "milwaukie-post",
      name: "Milwaukie Post Office",
      type: "USPS Passport Acceptance",
      address: "11122 SE Oak St",
      city: "Milwaukie",
      state: "OR",
      zip: "97222",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Milwaukie — about 6 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Milwaukie option for northern West Linn and Portland metro access.",
      icon: "ri-mail-line",
    },
    {
      id: "tualatin-post",
      name: "Tualatin Post Office",
      type: "USPS Passport Acceptance",
      address: "19250 SW Martinazzi Ave",
      city: "Tualatin",
      state: "OR",
      zip: "97062",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Tualatin — about 8 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tualatin option for southwestern West Linn and Washington County residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in West Linn before international travel?",
      a: "Yes. Through Fast Passport Center, West Linn travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute.",
    },
    {
      q: "How fast can I renew my passport in West Linn?",
      a: "Fast passport renewal in West Linn is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my trip to Europe?",
      a: "If your passport expires before your Europe trip, stop procrastinating and start now. European countries typically require your passport to be valid for at least 3–6 months beyond your travel dates. Fast Passport Center can expedite your passport renewal so you don't miss your flight. The sooner you start, the more options you have.",
    },
    {
      q: "Can I get a same-day passport appointment near West Linn?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near West Linn, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in West Linn?",
      a: "Yes. Child passport expediting in West Linn is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does West Linn have a passport agency?",
      a: "West Linn, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most West Linn residents.",
    },
    {
      q: "How long does a standard passport take in West Linn?",
      a: "If you apply through a standard post office acceptance facility in West Linn, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers West Linn travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in West Linn without visiting an office?",
      a: "Yes. Most West Linn-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in the West Linn or Portland metro area.",
    },
    {
      q: "What airports do West Linn passport holders typically fly from?",
      a: "Portland International Airport (PDX) is the primary airport for West Linn travelers, just 15 miles north and offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. Salem (SLE) and Eugene (EUG) are regional options. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — West Linn", address: "19133 Willamette Dr, West Linn, OR 97068", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Lake Oswego", address: "525 SW Mcvey Ave, Lake Oswego, OR 97034", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Lake Oswego", address: "1500 NW Bethany Blvd, Lake Oswego, OR 97034", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Tualatin", address: "19238 SW Martinazzi Ave, Tualatin, OR 97062", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — Milwaukie", address: "10830 SE Oak St, Milwaukie, OR 97222", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Clackamas", address: "16301 SE 82nd Dr, Clackamas, OR 97015", hours: "Daily 7 AM – 11 PM" },
    { name: "Costco — Tualatin", address: "7855 SW Dartmouth St, Tualatin, OR 97062", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — Oregon City", address: "19000 Molalla Ave, Oregon City, OR 97045", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Portland", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Tigard", address: "14865 SW 72nd Ave, Tigard, OR 97224", hours: "Daily 8 AM – 9 PM" },
  ],
  dmvLocations: [
    {
      name: "Oregon City Driver & Motor Vehicle Services",
      address: "1021 S Molalla Ave, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Closest DMV to West Linn. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Clackamas DMV Office",
      address: "5060 SE Hwy 212, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas County DMV option for West Linn residents.",
      featured: false,
    },
    {
      name: "Portland Driver & Motor Vehicle Services — McLoughlin",
      address: "9301 SE McLoughlin Blvd, Portland, OR 97222",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Portland DMV option for northern West Linn residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — 15 miles north",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the closest major airport for West Linn travelers, offering direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some West Linn travelers use SEA for additional flight options. Passport required for all international departures from either airport.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from West Linn and the Portland metro area.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help West Linn and Clackamas County travelers renew fast — passport expired before trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in West Linn", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide West Linn travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for West Linn families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for West Linn residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for West Linn travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for West Linn, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for West Linn residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for West Linn, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for West Linn travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for West Linn families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving West Linn, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "West Linn travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in West Linn." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of West Linn runs 6–13 weeks. If your trip to Europe, Asia, or Canada is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your West Linn travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from West Linn", description: "Drop off at any FedEx or UPS in the West Linn or Portland metro — most locations are open evenings and weekends. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from West Linn" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/portland" },
    { name: "Fast passport renewal in Oregon City", route: "/oregon-city" },
    { name: "Emergency passport help near Milwaukie", route: "/milwaukie" },
    { name: "Rush passport service in Lake Oswego", route: "/lake-oswego" },
    { name: "Fast passport renewal for Clackamas County travelers", route: "/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in West Linn or the Portland metro area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving West Linn and all of Clackamas County remotely",
  serviceAreaIntro: "Whether you are in West Linn, Lake Oswego, Oregon City, Milwaukie, or anywhere in Clackamas County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all West Linn and Clackamas County zip codes. Anywhere in the Portland metro south — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in West Linn as simple as possible. Most West Linn clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for West Linn travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from West Linn, Lake Oswego, and Oregon City travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions West Linn and Clackamas County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};